import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import { loginActions } from "src/redux/actions/auth";
import { LoginRequestTypes } from "src/redux/actions/auth/types";
import { StyledGriffleLogin } from "./GriffleLogin.styles";

import mascotImg from "src/assets/images/griffle/mainmascot.svg";
import greenBg from "src/assets/images/griffle/greenloginbackground.svg";
import cloudImg from "src/assets/images/griffle/cloud.svg";
import blobImg from "src/assets/icons/griffle/layout/griffle-blob.svg";

const loginSchema = Yup.object().shape({
  password: Yup.string().min(4, "Password is too short").max(50).required("Required"),
  username: Yup.string().min(3, "Username is too short").required("Required")
});

const GriffleLogin: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formError, setFormError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  useEffect(() => {
    document.body.classList.add("griffle-login-active", "griffle-page");
    return () => document.body.classList.remove("griffle-login-active", "griffle-page");
  }, []);

  const onSuccess = useCallback(() => {
    navigate("/griffle/dashboard");
  }, [navigate]);

  const sendLogin = useCallback((data: LoginRequestTypes) => dispatch(loginActions.request(data)), [dispatch]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const handleSubmit = (values, { setSubmitting }) => {
    setIsSubmittingForm(true);
    sendLogin({
      ...values,
      onSuccess,
      onError: (msg: string) => {
        setFormError(msg);
        setIsSubmittingForm(false);
      }
    });
    setSubmitting(false);
  };

  return (
    <StyledGriffleLogin>
      <div className="login-bg">
        <img src={greenBg} alt="" />
      </div>

      <div className="login-blob">
        <img src={blobImg} alt="" />
      </div>

      <div className="login-content">
        <span className="login-logo">GRIFFIN</span>

        <h1 className="login-heading">Welcome to Griffin&apos;s Reading Adventure!</h1>

        <Formik initialValues={{ username: "", password: "" }} validationSchema={loginSchema} onSubmit={handleSubmit}>
          {({ isSubmitting }) => (
            <Form onChange={() => setFormError(null)}>
              {(isSubmitting || isSubmittingForm) && (
                <div className="login-loader">
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      border: "3px solid var(--color-primary)",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 0.7s linear infinite"
                    }}
                  />
                </div>
              )}

              <div className="login-field">
                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" strokeLinecap="round" />
                </svg>
                <Field id="username" name="username" type="text" placeholder="USERNAME" className="login-input" />
                <ErrorMessage name="username">{(msg) => <div className="field-error">{msg}</div>}</ErrorMessage>
              </div>

              <div className="login-field">
                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2">
                  <rect x="5" y="11" width="14" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" strokeLinecap="round" />
                </svg>
                <Field id="password" name="password" type="password" placeholder="PASSWORD" className="login-input" />
                <ErrorMessage name="password">{(msg) => <div className="field-error">{msg}</div>}</ErrorMessage>
              </div>

              {formError && <div className="form-error">{formError}</div>}

              <label className="login-remember">
                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                Remember me
              </label>

              <div className="login-actions">
                <button type="submit" className="login-btn" disabled={isSubmitting || isSubmittingForm}>
                  LOGIN
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <div className="login-mascot">
        <img src={mascotImg} alt="Griffin mascot" />
      </div>

      <div className="login-cloud">
        <img src={cloudImg} alt="" />
      </div>
    </StyledGriffleLogin>
  );
};

export default GriffleLogin;
